/**
 * Centralized event registry with all available events
 * Use this for easy access and to check available events
 */
export const BRIDGE_EVENTS = {
    INIT_WEBPHONE: "INIT_WEBPHONE",
    SHOW_WEBPHONE: "SHOW_WEBPHONE",
    TOGGLE_WEBPHONE: "TOGGLE_WEBPHONE",
    INIT_MEDIA: "INIT_MEDIA",
    MEDIA_READY: "MEDIA_READY",
    WEBPHONE_CONFIG: "WEBPHONE_CONFIG",
    WEBPHONE_FORMATTING: "WEBPHONE_FORMATTING",
    WEBPHONE_FORMATTING_USER: "WEBPHONE_FORMATTING_USER",
    WEBPHONE_CONNECTED: "WEBPHONE_CONNECTED",
    WEBPHONE_DATE_FORMAT: "WEBPHONE_DATE_FORMAT",
    WEB_DEVICE_IS_ONLINE: "WEB_DEVICE_IS_ONLINE",
    AGENT_PAUSE_ID: "AGENT_PAUSE_ID",
    USER_IS_CHECKED_IN: "USER_IS_CHECKED_IN",
    AGENT_STATS_CHANGED: "AGENT_STATS_CHANGED",
    USER_CALL: "USER_CALL",
    WEBPHONE_EVENT: "WEBPHONE_EVENT",
    WEBPHONE_OPEN_STATE: "WEBPHONE_OPEN_STATE",
    WEBPHONE_ACTION_RESPONSE: "WEBPHONE_RESPONSE",
    WEBPHONE_ACTION_REQUEST: "WEBPHONE_REQUEST",
    WEBPHONE_PIP_SETTINGS_UPDATE: "WEBPHONE_PIP_SETTINGS_UPDATE",
    INITIATE_WEBPHONE_CALL: "INITIATE_WEBPHONE_CALL",
    SHOW_NOTIFICATION: "SHOW_NOTIFICATION",
    CALL_STATUS_UPDATE: "CALL_STATUS_UPDATE",
    BROWSER_NOTIFICATION: "BROWSER_NOTIFICATION",
    CHECK_IN_CHAT: "CHECK_IN_CHAT",
    CONFIG_LOADED: "CONFIG_LOADED",
    MESSENGER_NOTIFICATION_CLICK: "MESSENGER_NOTIFICATION_CLICK",
    WEBPHONE_CALLS_STATE: "WEBPHONE_CALLS_STATE",
    WEBPHONE_READY: "WEBPHONE_READY",
    SHOW_TRANSCRIPTION_MODAL: "SHOW_TRANSCRIPTION_MODAL",
    LISTEN_TO_CALL: "LISTEN_TO_CALL",
    TERMINATE_ALL_CALLS: "TERMINATE_ALL_CALLS",
    MONITORING_STATE_UPDATE: "MONITORING_STATE_UPDATE",
    MONITORING_ACTION: "MONITORING_ACTION",
    WEBPHONE_REQUEST_POP_OUT: "WEBPHONE_REQUEST_POP_OUT",
    SHOW_CONFIRM_DIALOG: "SHOW_CONFIRM_DIALOG",
    CONFIRM_DIALOG_RESPONSE: "CONFIRM_DIALOG_RESPONSE",
    SHOW_CONTACT_DETAILS: "SHOW_CONTACT_DETAILS",
    SHOW_CONTACT_HISTORY: "SHOW_CONTACT_HISTORY",
    HIDE_CONTACT_HISTORY: "HIDE_CONTACT_HISTORY",
    CONTACT_HISTORY_REOPENABLE: "CONTACT_HISTORY_REOPENABLE",
    VIEWING_OLDER_CHAT: "VIEWING_OLDER_CHAT",
    CONTACT_DELETED: "CONTACT_DELETED",
    WEBPHONE_REPROVISION_NEEDED: "WEBPHONE_REPROVISION_NEEDED",
    WEBPHONE_UPDATE_DEVICE: "WEBPHONE_UPDATE_DEVICE",
    MESSENGER_IS_CRAMPED: "MESSENGER_IS_CRAMPED",
    CONTACT_HISTORY_DATA: "CONTACT_HISTORY_DATA",
    OPEN_CLOSED_CHAT: "OPEN_CLOSED_CHAT",
    CLOSE_CONTACT_HISTORY: "CLOSE_CONTACT_HISTORY",
    INTERNAL_CHAT_DETAILS_DATA: "INTERNAL_CHAT_DETAILS_DATA",
    CLOSE_INTERNAL_DETAILS: "CLOSE_INTERNAL_DETAILS",
};

/**
 * CrossFrameworkBridge - Singleton pattern for type-safe communication
 * between React and Vue.js components via CustomEvents
 *
 * Features:
 * - Type-safe event emission and listening
 * - Automatic cleanup via returned unsubscribe functions
 * - Centralized event management
 *
 * @example
 * // Emitting events using BRIDGE_EVENTS
 * bridge.emit(BRIDGE_EVENTS.USER_CALL, { phone_number: '+1234567890', should_auto_call: true });
 *
 * @example
 * // Listening to events
 * const unsubscribe = bridge.on(BRIDGE_EVENTS.SHOW_WEBPHONE, () => {
 *   console.log('Webphone shown');
 * });
 * // Later: unsubscribe();
 */
export class CrossFrameworkBridge {
    static instance = null;

    constructor() {
        if (typeof window !== "undefined") {
        }
    }

    static getInstance() {
        if (!this.instance) {
            this.instance = new CrossFrameworkBridge();
        }
        return this.instance;
    }

    /**
     * Emit a type-safe event across frameworks
     *
     * @param {string} event - The event name (use constants from eventUtils)
     * @param {*} detail - The event payload
     */
    emit(event, detail) {
        if (typeof window === "undefined") {
            return;
        }

        const customEvent = new CustomEvent(event, {
            detail: detail,
        });

        window.dispatchEvent(customEvent);
    }

    /**
     * Listen to a type-safe event from other frameworks
     *
     * @param {string} event - The event name (use constants from eventUtils)
     * @param {Function} handler - Callback function with typed detail parameter
     * @param {AddEventListenerOptions} options - Standard addEventListener options
     * @returns {Function} Unsubscribe function to remove the listener
     */
    on(event, handler, options) {
        if (typeof window === "undefined") {
            return () => {};
        }

        const listener = (e) => {
            handler(e.detail);
        };

        window.addEventListener(event, listener, options);

        return () => {
            window.removeEventListener(event, listener);
        };
    }

    /**
     * Listen to an event once and automatically unsubscribe
     *
     * @param {string} event - The event name
     * @param {Function} handler - Callback function
     */
    once(event, handler) {
        if (typeof window === "undefined") {
            return;
        }

        const listener = (e) => {
            handler(e.detail);
        };

        window.addEventListener(event, listener, { once: true });
    }
}

/**
 * Singleton instance of the bridge - use this for all cross-framework communication
 */
export const bridge = CrossFrameworkBridge.getInstance();
